import type { CaptureKind, Item } from '@mystiq/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { keys } from '../api/hooks.ts';
import { useApi, useSession } from '../auth/session.tsx';
import { ICON, Mic, Pause, Play, Stop } from '../icons.ts';
import { formatDuration } from '../lib/format.ts';
import { newId, Recorder, recordingsInProgress, type CaptureSource, type FinishedRecording, type RecorderState } from '../media/recorder.ts';
import { openRecordingsStore, type RecordingsStore, type StoredRecording } from '../media/recordings-store.ts';
import { saveRecording } from '../media/save-recording.ts';
import { UploadButton, UploadStatus, useUploader } from './Upload.tsx';

/** A recording that is not in Mystiq yet: cut off, interrupted, or its upload failed. */
type Unsaved = { recording: StoredRecording; reason: string };

const WORDS: Record<CaptureKind, { record: string; noun: string }> = {
  voice_note: { record: 'Record voice note', noun: 'voice note' },
  meeting: { record: 'Record meeting', noun: 'meeting' },
};

/**
 * Records a voice note or a meeting in the browser, keeps it in this browser
 * second by second, then saves it to Mystiq, where it is transcribed. Every
 * failure is a plain state with what to do next, and nothing recorded is
 * thrown away unless the person says so.
 */
export function RecordingPanel({ ws, capture, onSaved }: { ws: string; capture: CaptureKind; onSaved(item: Item): void }) {
  const api = useApi();
  const session = useSession();
  const client = useQueryClient();
  const [store, setStore] = useState<RecordingsStore | null | undefined>(undefined);
  const [state, setState] = useState<RecorderState>({ status: 'idle' });
  const [source, setSource] = useState<CaptureSource>('microphone');
  const [unsaved, setUnsaved] = useState<Unsaved[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<{ id: string; message: string } | null>(null);
  const recorder = useRef<Recorder | null>(null);
  const uploader = useUploader(ws, undefined, capture);

  const refreshUnsaved = useCallback(
    async () => {
      const found = store ? await store.list(ws, session.userKey).catch(() => []) : [];
      // Recordings still being made (here, or in another tab) are not "unsaved".
      const live = await recordingsInProgress();
      const activeId = recorder.current?.activeId;
      setUnsaved(
        found
          .filter((recording) => recording.capture === capture && recording.id !== activeId && !live.has(recording.id))
          .map((recording) => ({ recording, reason: recording.state === 'recording' ? 'Cut off before it was stopped (the page closed or the browser stopped).' : 'Not saved to Mystiq yet.' })),
      );
    },
    [store, ws, session.userKey, capture],
  );

  useEffect(() => {
    void openRecordingsStore().then(setStore);
  }, []);
  useEffect(() => {
    if (store !== undefined) void refreshUnsaved();
  }, [store, refreshUnsaved]);

  // Going to another Mystiq page stops recording (or a start still waiting for the microphone);
  // what was recorded stays in the browser, offered for saving on return.
  useEffect(() => () => recorder.current?.dispose(), []);

  // Leaving while recording asks first (what was recorded is kept either way).
  useEffect(() => {
    if (state.status !== 'recording' && state.status !== 'finalizing') return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [state.status]);

  const save = async (recording: Pick<FinishedRecording, 'id' | 'blob' | 'title' | 'filename' | 'capture' | 'uploadKey'>) => {
    setSaving(recording.id);
    setSaveError(null);
    try {
      const item = await saveRecording(api, ws, recording, {
        // An upload that can no longer take the bytes (it ran out) is started again under a new key, kept with the recording.
        renewKey: async () => {
          const uploadKey = `recording-${newId()}`;
          await store?.update(recording.id, { uploadKey }).catch(() => undefined);
          return uploadKey;
        },
      });
      await store?.remove(recording.id).catch(() => undefined);
      void client.invalidateQueries({ queryKey: keys.ws(ws) });
      onSaved(item);
    } catch (error) {
      setSaveError({ id: recording.id, message: (error as Error).message });
      await refreshUnsaved();
    } finally {
      setSaving(null);
    }
  };

  const start = async () => {
    if (store === undefined) return;
    setSaveError(null);
    const instance = new Recorder({
      store,
      workspaceId: ws,
      userKey: session.userKey,
      capture,
      onChange: setState,
      onInterrupted: (recording, message) => {
        setSaveError({ id: recording.id, message: `${message} What was recorded (${formatDuration(recording.durationMs)}) is kept.` });
        void refreshUnsaved();
      },
    });
    recorder.current = instance;
    await instance.start(capture === 'meeting' ? source : 'microphone');
    void refreshUnsaved();
  };

  const stopAndSave = async () => {
    const instance = recorder.current;
    if (!instance) return;
    const recording = await instance.stop();
    instance.reset();
    await save(recording);
  };

  const recordingNow = state.status === 'recording' || state.status === 'permission' || state.status === 'finalizing';
  return (
    <div className="stack">
      {capture === 'meeting' && !recordingNow && (
        <fieldset className="field">
          <legend className="field-label">What to record</legend>
          <label className="choice">
            <input type="radio" name="meeting-source" checked={source === 'microphone'} onChange={() => setSource('microphone')} />
            <span>
              The microphone <span className="meta">(a meeting in the room, or a call on speaker)</span>
            </span>
          </label>
          <label className="choice">
            <input type="radio" name="meeting-source" checked={source === 'tab'} onChange={() => setSource('tab')} />
            <span>
              The microphone and a browser tab's sound <span className="meta">(a call in Chrome or Edge: choose the tab and turn on "Share tab audio")</span>
            </span>
          </label>
          <span className="meta">Sound from desktop apps (Zoom, Teams) is recorded only if your microphone hears it. Speakers are not identified in the transcript.</span>
        </fieldset>
      )}

      {!recordingNow && (
        <div className="toolbar" style={{ marginTop: 0 }}>
          <button className="button button-primary" onClick={() => void start()} disabled={store === undefined}>
            <Mic size={ICON.nav} stroke={ICON.stroke} />
            {WORDS[capture].record}
          </button>
          <UploadButton label="Upload a recording" accept="audio/*,video/*" onFiles={(files) => void uploader.upload(files)} />
        </div>
      )}
      <UploadStatus active={uploader.active} errors={uploader.errors} onDismiss={uploader.clearErrors} />

      {state.status === 'permission' && (
        <p className="meta" role="status">
          <span className="state">Asking for the microphone.</span> {state.source === 'tab' ? 'Allow the microphone, then choose the tab to record and turn on its sound.' : "Allow it in your browser's prompt."}
        </p>
      )}
      {state.status === 'recording' && (
        <div className="recorder" role="region" aria-label="Recording">
          <p className="recorder-line" role="status">
            <span className="now-dot" aria-hidden />
            <span className="state">{state.paused ? 'Paused' : 'Recording'}</span>
            <span className="recorder-time">{formatDuration(state.elapsedMs)}</span>
            <span className="level" aria-hidden>
              <span style={{ width: `${Math.round(state.level * 100)}%` }} />
            </span>
          </p>
          <div className="row-actions">
            {state.paused ? (
              <button className="button" onClick={() => recorder.current?.resume()}>
                <Play size={ICON.nav} stroke={ICON.stroke} />
                Resume
              </button>
            ) : (
              <button className="button" onClick={() => recorder.current?.pause()}>
                <Pause size={ICON.nav} stroke={ICON.stroke} />
                Pause
              </button>
            )}
            <button className="button button-primary" onClick={() => void stopAndSave()}>
              <Stop size={ICON.nav} stroke={ICON.stroke} />
              Stop and save
            </button>
            <button
              className="button"
              onClick={async () => {
                await recorder.current?.discard();
                void refreshUnsaved();
              }}
            >
              Discard
            </button>
          </div>
          <p className="meta">{state.notice ?? 'Kept in this browser every second as you record, so closing the tab does not lose it.'}</p>
        </div>
      )}
      {(state.status === 'finalizing' || saving) && (
        <p className="meta" role="status">
          <span className="state">Saving the {WORDS[capture].noun}.</span> It is transcribed as soon as it is saved.
        </p>
      )}
      {state.status === 'error' && (
        <p role="alert">
          <span className="state">Not recording.</span> {state.message}
        </p>
      )}

      {unsaved.length > 0 && (
        <section className="stack" aria-label="Recordings not saved yet">
          {unsaved.map(({ recording, reason }) => (
            <div className="conflict" key={recording.id}>
              <p>
                <span className="state">Not saved yet:</span> {recording.title ?? recording.filename.replace(/\.\w+$/, '')}
                {recording.durationMs ? ` (${formatDuration(recording.durationMs)})` : ''}. {saveError?.id === recording.id ? saveError.message : reason}
              </p>
              <div className="row-actions">
                <button
                  className="button"
                  disabled={saving === recording.id}
                  onClick={async () => {
                    if (!store) return;
                    const blob = await store.blob(recording.id);
                    await save({
                      id: recording.id,
                      blob,
                      title: recording.title ?? recording.filename.replace(/\.\w+$/, ''),
                      filename: recording.filename,
                      capture: recording.capture,
                      uploadKey: recording.uploadKey,
                    });
                  }}
                >
                  Save it
                </button>
                <button
                  className="button"
                  onClick={async () => {
                    if (!store) return;
                    // A copy to keep, whatever happens to the connection.
                    const url = URL.createObjectURL(await store.blob(recording.id));
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = recording.filename;
                    link.click();
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                  }}
                >
                  Download a copy
                </button>
                <button
                  className="button"
                  disabled={saving === recording.id}
                  onClick={async () => {
                    await store?.remove(recording.id).catch(() => undefined);
                    if (saveError?.id === recording.id) setSaveError(null);
                    void refreshUnsaved();
                  }}
                >
                  Discard
                </button>
              </div>
            </div>
          ))}
        </section>
      )}
      {saveError && !unsaved.some((entry) => entry.recording.id === saveError.id) && (
        <p role="alert">
          <span className="state">Not saved.</span> {saveError.message}
        </p>
      )}
    </div>
  );
}
